import { supabase } from '../../config/supabase';
import type { ProfileRole } from '../../types/database';
import { ProfileAdditionalRole, type ProfileRoleList } from '../../types/profileRoles';

function normalizeProfileRoles(rows: Array<{ role: string }> | null | undefined): ProfileRoleList {
  if (!rows || rows.length === 0) {
    return [];
  }

  const allowed = new Set<string>(Object.values(ProfileAdditionalRole));
  return rows
    .map((row) => row.role)
    .filter((role): role is ProfileAdditionalRole => allowed.has(role))
    .map((role) => role as ProfileAdditionalRole);
}

export const profileRoleService = {
  async getProfileRoles(userId: string): Promise<ProfileRoleList> {
    if (!userId) {
      return [];
    }

    const { data, error } = await supabase
      .from('profile_roles')
      .select('role')
      .eq('profile_id', userId);

    if (error) {
      console.error('profileRoleService.getProfileRoles error:', error);
      throw new Error(error.message || 'Failed to load profile roles');
    }

    return normalizeProfileRoles(data as Pick<ProfileRole, 'role'>[] | null);
  },

  async listDeveloperProfileIds(): Promise<Set<string>> {
    const { data, error } = await supabase
      .from('profile_roles')
      .select('profile_id')
      .eq('role', ProfileAdditionalRole.Dev);

    if (error) {
      console.error('profileRoleService.listDeveloperProfileIds error:', error);
      throw new Error(error.message || 'Failed to load developer access');
    }

    const rows = (data ?? []) as Pick<ProfileRole, 'profile_id'>[];
    return new Set(rows.map((row) => row.profile_id));
  },

  async setDeveloperAccess(userId: string, enabled: boolean): Promise<void> {
    const { error } = await supabase.rpc('admin_set_profile_dev', {
      target_user: userId,
      enabled,
    });

    if (error) {
      console.error('profileRoleService.setDeveloperAccess error:', error);
      throw new Error(error.message || 'Failed to update developer access');
    }
  },
};
